import React, { useState, useEffect } from 'react';
import { flatMap } from '../../../../../Util';
import { ProductRequestResult, ProductRequestResultProduct, SearchRequest } from '../../../../../Services/SearchProductFeedTypes';
import { ShopifyProductCollection } from '../../../../../Services/ShopifyManualSortingServices';
import Btn from '../../../../../Components/Btn';
import IconBtn from '../../../../../Components/IconBtn';
import Modal from '../../../../../Components/Modal';
import ModalHeader from '../../../../../Components/ModalHeader';
import ModalBody from '../../../../../Components/ModalBody';
import ModalSection from '../../../../../Components/ModalSection';
import ModalFooter from '../../../../../Components/ModalFooter';
import SelectDropdown from '../../../../../Components/SelectDropdown';
import { EmptyState } from '../../../../../Components/EmptyState/EmptyState';
import SearchBar from '../../../../../Components/SearchBar';
import { ProductThumb } from '../../../../../Components/Product/ProductThumb';
import NumericUpDown from '../../../../../Components/NumericUpDown';
import FieldRow from '../../../../../Components/FieldRow';
import { Article } from '../../../../../Components/Article/Article';
import { ListItem } from '../../../../../Components/List';
import { NameLabel } from '../../../Ranking/context/RankingContextProvider';
import { pinId } from '../../../Ranking/utils';
import SearchProducts from '../../SearchProducts';
import { PinnedShopifyProduct, ShopifyCollectionWithPinnedProducts } from './ShopifyManualSortingTypes';


type ShopifyManualSortingProductPositionSelectorType = {
	showPopin: { show: boolean, edit?: { collectionId: string, product: PinnedShopifyProduct }, addSearchProduct?: ProductRequestResultProduct },
	handleClose: () => void,
	shopifyCollections: ShopifyProductCollection[],
	collectionWithPinnedProducts: ShopifyCollectionWithPinnedProducts[],
	typeCategoriesSecondLevelSelected: NameLabel | undefined,
	selectedCategoryIsShopifyCollection: { isPinnableShopifyCollection: boolean, collectionId?: string },
	handleConfirm: (selectedCollection: ShopifyProductCollection, selectedProduct: PinnedShopifyProduct) => void,
	searchProducts: (search: SearchRequest, callbackSuccess: ((data: ProductRequestResult) => void), callbackError?: ((error: string) => void)) => void,
}

export default function ShopifyManualSortingProductPositionSelector({
	handleClose,
	showPopin,
	shopifyCollections,
	collectionWithPinnedProducts,
	typeCategoriesSecondLevelSelected,
	selectedCategoryIsShopifyCollection,
	handleConfirm,
	searchProducts
}: ShopifyManualSortingProductPositionSelectorType) {

	const [searchTerm, setSearchTerm] = useState('');
	const [searchTermForInput, setSearchTermForInput] = useState('');
	const [step, setStep] = useState(1);
	const [selectedCollection, setSelectedCollection] = useState<ShopifyProductCollection | undefined>(undefined);
	const [selectedProduct, setSelectedProduct] = useState<PinnedShopifyProduct | undefined>(undefined);
	const [allowStepBack, setAllowStepBack] = useState<boolean>(true);
	const [selectedSearchProduct, setSelectedSearchProduct] = useState<ProductRequestResultProduct | undefined>(undefined);
	const [selectableCollections, setSelectableCollections] = useState<ShopifyProductCollection[]>([]);

	const [isError, setIsError] = useState<{ isError: boolean, error?: string | null }>({ isError: false });

	isError; // display error somewhere

	useEffect(() => {
		const searchTimeout = setTimeout(() => {
			setSearchTerm(searchTermForInput);
		}, 500);

		return () => {
			clearTimeout(searchTimeout);
		};
	}, [searchTermForInput]);

	const confirmSelection = () => {
		if (!selectedCollection || !selectedProduct || isNaN(selectedProduct.position)) return;
		handleConfirm(selectedCollection, selectedProduct);
		handleClose();
	};

	useEffect(() => {
		if (!showPopin.show)
			return;

		setSearchTerm('');
		setSearchTermForInput('');

		const getFirstShopifyCollection = () => shopifyCollections[0];
		if (showPopin.edit) {
			setAllowStepBack(false);
			setStep(2);
			const collection = shopifyCollections.find(x => x.id === showPopin.edit?.collectionId);
			if (!collection) {
				console.log('Cannot find collection: ' + showPopin.edit?.collectionId);
				setIsError({ isError: true, error: 'Cannot find collection: ' + showPopin.edit?.collectionId });
				return;
			}
			setSelectedCollection(collection);
			setSelectedProduct(showPopin.edit.product);
			setSelectedSearchProduct(undefined);

			setSelectableCollections(shopifyCollections.filter(c => showPopin.edit?.product.product.categories?.find(x => x._name === c.handle)));

		} else if (showPopin.addSearchProduct) {
			setAllowStepBack(false);
			setStep(0);

			const getFirstShopifyCollectionOfProduct = () => shopifyCollections
				.find(x => x.handle === showPopin.addSearchProduct?.categories?.find(c => c._type === 'collection')?._name);

			const collection = (selectedCategoryIsShopifyCollection.isPinnableShopifyCollection ?
				shopifyCollections
					.find(x => x.handle === showPopin.addSearchProduct?.categories?.find(x => x._name === typeCategoriesSecondLevelSelected?.name)?._name)
				: undefined) ?? getFirstShopifyCollectionOfProduct();

			if (!collection) {
				console.log('Cannot find collection');
				setIsError({ isError: true, error: 'Cannot find collection' });
				return;
			}
			setSelectedCollection(collection);
			setSelectedProduct({ product: showPopin.addSearchProduct, position: 0 });
			setSelectedSearchProduct(undefined);

			setSelectableCollections(shopifyCollections.filter(c => showPopin.addSearchProduct?.categories?.find(x => x._name === c.handle)));
		}
		else {
			setAllowStepBack(true);
			setStep(1);
			const collection = shopifyCollections.find(x => x.handle === typeCategoriesSecondLevelSelected?.name);
			setSelectedCollection(collection ?? getFirstShopifyCollection());
			setSelectedProduct(undefined);
			setSelectedSearchProduct(undefined);
			setSelectableCollections(shopifyCollections);
		}
	}, [showPopin]);

	const changeSelectedCollection = (value: string) => {
		const collection = shopifyCollections.find(x => x.handle === value);
		setSelectedCollection(collection);
	};

	const selectProduct = (p: ProductRequestResultProduct) => {
		setSelectedProduct({ product: p, position: 0 });
		setStep(2);
	};


	const selectProductStep = () => (
		<>
			<ModalSection>
				{selectedSearchProduct &&
					<>
						<div className='mb_20'>
							<Article color='grey' innerSize='s' hasMargin={false}>
								<ListItem
									key={selectedSearchProduct.id}
									text={selectedSearchProduct.name}
									textHasEllipsis={true}
									textIsBold={false}
									description={selectedSearchProduct.id}
									picto={
										<ProductThumb
											url={selectedSearchProduct.urlThumbnail || ''}
											alt={selectedSearchProduct.name}
											hasMargin={false}
											width={40}
											backgroundColor={'white'}
										/>
									}
								/>
							</Article>
						</div>
						<SelectDropdown
							label="Category"
							labelPosition="inner"
							optionsList={selectableCollections.map(x => ({ value: x.handle, label: x.handle }))}
							value={selectedCollection?.handle ?? ''}
							onChange={(v) => changeSelectedCollection(v)}
							fullWidth={true}
						/>
					</>
				}
				{!selectedSearchProduct &&
					<div className='flex' style={{gap: 10}}>
						<div className='flex_item_half'>
							<SelectDropdown
								label="Category"
								labelPosition="inner"
								optionsList={selectableCollections.map(x => ({ value: x.handle, label: x.handle }))}
								value={selectedCollection?.handle ?? ''}
								onChange={(v) => changeSelectedCollection(v)}
								fullWidth={true}
							/>
						</div>
						<div className='flex_item_half'>
							<SearchBar
								value={searchTermForInput}
								onChange={v => setSearchTermForInput(v)}
								placeholder={'Search in product catalog'}
								fullWidth={true}
								size="l"
							/>
						</div>
					</div>
				}
			</ModalSection>
			<ModalBody hasTopPadding={false} position='relative'>
				{!selectedSearchProduct && !searchTerm &&
					<EmptyState
						text={'Use the search field to select a product'}
						textSize='l'
						width='s'
						verticalAlign={true}
					/>
				}
				<SearchProducts
					dimension={pinId}
					onAddProduct={(p: ProductRequestResultProduct) => selectProduct(p)}
					searchTerm={searchTerm}
					searchProductsByTerm={(s, e) => searchProducts({
						searchTerm: searchTerm ?? '',
						category: {
							type: { equals: 'collection' },
							name: { equals: selectedCollection?.handle }
						}
					}, s, e)}
					arrayOfIdsToExcludes={flatMap(collectionWithPinnedProducts, x => x.pinnedProducts.map(p => p.product.id))}
					allProduct={true}
					deleteList={undefined}
					searchDependency={selectedCollection}
					size={'s'}
					showTitle={false}
				/>
			</ModalBody>
		</>
	);

	const selectPositionStep = (needSearchCollection) => (selectedCollection || needSearchCollection) && selectedProduct && (
		<>
			<ModalBody>
				<div className='mb_20'>
					<Article color='grey' innerSize='s' hasMargin={false}>
						{selectedCollection && !needSearchCollection &&
							<div style={{borderBottom: 'solid 1px var(--main-border-color)', paddingBottom: 10, marginBottom: 10, color: 'var(--grey-0)'}}>
								<span className='fw_medium'>Collection:</span> {selectedCollection.handle}
							</div>
						}
						<ListItem
							key={selectedProduct.product.id}
							text={selectedProduct.product.name}
							textHasEllipsis={true}
							textIsBold={false}
							description={selectedProduct.product.id}
							picto={
								<ProductThumb
									url={selectedProduct.product.urlThumbnail || ''}
									alt={selectedProduct.product.name}
									hasMargin={false}
									width={40}
									backgroundColor={'white'}
								/>
							}
						/>
					</Article>
				</div>
				{needSearchCollection &&
					<FieldRow label="Collection" labelWidth="full">
						<SelectDropdown
							label="Category"
							labelPosition="inner"
							optionsList={selectableCollections.map(x => ({ value: x.handle, label: x.handle }))}
							value={selectedCollection?.handle ?? ''}
							onChange={(v) => changeSelectedCollection(v)}
							fullWidth={true}
						/>
					</FieldRow>
				}
				<FieldRow label="New position in collection" labelWidth="full">
					<NumericUpDown
						width="200"
						min={1}
						canEdit={true}
						value={isNaN(selectedProduct.position) ? '' : selectedProduct.position + 1}
						onChange={(e) => setSelectedProduct(({ ...selectedProduct, position: e.target.value - 1 }))}
					/>
				</FieldRow>
			</ModalBody>
			<ModalFooter
				primaryAction={
					<Btn
						onClick={() => confirmSelection()}
						message="Confirm"
					/>
				}
			/>
		</>
	);

	return (
		<Modal
			isOpen={showPopin.show}
			width={500}
			height={step === 1 ? 640 : null}
			onClose={() => handleClose()}
		>
			<ModalHeader
				title="Pin product"
				hasBorder={false}
				hasBottomPadding={false}
				backButton={
					step > 1 && allowStepBack && (
						<IconBtn
							icon='fas fa-arrow-left'
							isActive={true}
							onClick={() => setStep(s => s - 1)}
						/>
					)
				}
			/>
			{step === 0 && selectPositionStep(true)}
			{step === 1 && selectProductStep()}
			{step === 2 && selectPositionStep(false)}
		</Modal>
	);
}
